import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { handleAuthCallback, userManager, API, isMasterAdmin, consumeReauthReturnUrl } from '@sportschool/shared-logic';
import { Button } from '@/components/ui/button';

let _didNavigate = false;

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    handleAuthCallback()
      .then(async (callbackProcessed) => {
        if (!callbackProcessed || _didNavigate) return;

        // Step-up login (e.g. Statistics) goes straight back to the page that asked for it.
        const reauthReturnUrl = consumeReauthReturnUrl();
        if (reauthReturnUrl && reauthReturnUrl.startsWith('/') && !reauthReturnUrl.startsWith('//')) {
          _didNavigate = true;
          navigate(reauthReturnUrl, { replace: true });
          return;
        }

        const user = await userManager.getUser();
        if (user && !user.expired && isMasterAdmin(user.access_token)) {
          _didNavigate = true;
          navigate('/admin', { replace: true });
          return;
        }

        // Handle pending school join (user clicked an invite link before logging in)
        const joinSchoolId = localStorage.getItem('join_school_id') || sessionStorage.getItem('join_school_id');
        if (joinSchoolId) {
          localStorage.removeItem('join_school_id');
          sessionStorage.removeItem('join_school_id');
          try {
            await API.selfJoinSchool(joinSchoolId, { skipAuthRedirect: true });
          } catch {
            // Join may fail if already a member — proceed to dashboard anyway
          }
          _didNavigate = true;
          const returnUrl = localStorage.getItem('auth_return_url') || sessionStorage.getItem('auth_return_url');
          localStorage.removeItem('auth_return_url');
          sessionStorage.removeItem('auth_return_url');
          navigate(returnUrl || `/schools/${joinSchoolId}/dashboard`, { replace: true });
          return;
        }

        // Try the stored return URL first
        const returnUrl = localStorage.getItem('auth_return_url') || sessionStorage.getItem('auth_return_url') || '';
        const isExplicitCreateSchool = returnUrl.includes('/schools/new') || returnUrl.includes('/school/new');
        localStorage.removeItem('auth_return_url');
        sessionStorage.removeItem('auth_return_url');

        if (returnUrl && !returnUrl.startsWith('/auth') && !isExplicitCreateSchool && returnUrl !== '/') {
          _didNavigate = true;
          navigate(returnUrl, { replace: true });
          return;
        }

        // Fetch user's schools to decide where to send them
        try {
          const res = await API.listMySchools({ skipAuthRedirect: true });
          const schools: { id: string; name: string }[] = res as unknown as { id: string; name: string }[];
          _didNavigate = true;
          if (schools.length > 1) {
            navigate('/select-school', { replace: true });
          } else if (schools.length === 1) {
            navigate(`/schools/${schools[0].id}/dashboard`, { replace: true });
          } else {
            if (isExplicitCreateSchool) {
              navigate('/schools/new', { replace: true });
            } else {
              navigate('/select-school', { replace: true });
            }
          }
        } catch {
          _didNavigate = true;
          if (isExplicitCreateSchool) {
            navigate('/schools/new', { replace: true });
          } else {
            navigate('/select-school', { replace: true });
          }
        }
      })
      .catch((err) => {
        if (_didNavigate) return;
        console.error('Auth callback failed:', err);
        setError(err?.message || t('auth.callbackFailed'));
      });

    // If no callback params are present (page refresh, direct visit) but the
    // user is already logged in, redirect them immediately.
    const params = new URLSearchParams(window.location.search);
    if (!params.has('code') && !params.has('state')) {
      userManager.getUser().then((user) => {
        if (user && !user.expired && !_didNavigate) {
          _didNavigate = true;
          // Consume any stale return URL
          const returnUrl = localStorage.getItem('auth_return_url') || sessionStorage.getItem('auth_return_url');
          localStorage.removeItem('auth_return_url');
          sessionStorage.removeItem('auth_return_url');
          if (isMasterAdmin(user.access_token)) {
            navigate('/admin', { replace: true });
          } else {
            navigate(returnUrl || '/', { replace: true });
          }
        } else if (!_didNavigate) {
          _didNavigate = true;
          navigate('/', { replace: true });
        }
      });
    }

    return () => { };
  }, [navigate, t]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-4 text-center bg-background">
        <div className="w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center text-destructive mb-6">
          <AlertTriangle className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-semibold text-foreground mb-2">{t('auth.callbackFailedTitle')}</h2>
        <p className="text-sm text-muted-foreground max-w-md mb-8">{error}</p>
        <Button onClick={() => navigate('/')}>
          {t('auth.goHome')}
        </Button>
      </div>
    );
  }

  return <LoadingSpinner fullScreen />;
}

